import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaTimesCircle } from "react-icons/fa";
import { getDiscountedPrice } from "../utils/price.tsx";

interface ProductCardProps {
  id: string;
  name: string;
  imageUrl: string;
  price: number;
  discountRate?: number;
  campaignName?: string;
  campaignEndDate?: string;
  brand?: string;
  category?: string;
  participantCount?: number;
  maxUserCount?: number;
  isAuthenticated: boolean;
  hasCampaign: boolean;
  stock: number;
  onAddToCart: (quantity: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  imageUrl,
  price,
  discountRate,
  campaignName,
  campaignEndDate,
  brand,
  category,
  participantCount,
  maxUserCount,
  isAuthenticated,
  hasCampaign,
  stock,
  onAddToCart,
}) => {
  const [countdown, setCountdown] = useState("");
  const [quantity, setQuantity] = useState(1);
  const isOutOfStock = stock <= 0;
  const canAddToCart = (isAuthenticated || !hasCampaign) && !isOutOfStock;

  // Input değiştiğinde stok kontrolü
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = Number(e.target.value);
    if (val > stock) val = stock;
    if (val < 1) val = 1;
    setQuantity(val);
  };
  // Sepete eklemeden önce de kontrol
  const handleAddToCart = () => {
    const validQuantity = Math.min(quantity, stock);
    setQuantity(validQuantity); // inputu da düzelt
    onAddToCart(validQuantity);
  };

  useEffect(() => {
    if (!campaignEndDate) return;
    const interval = setInterval(() => {
      const now = new Date();
      const end = new Date(campaignEndDate);
      const diff = end.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown("Süre doldu");
        clearInterval(interval);
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown(`${hours}s ${minutes}d ${seconds}sn`);
    }, 1000);
    return () => clearInterval(interval);
  }, [campaignEndDate]);

  const discountedPrice = discountRate ? getDiscountedPrice(price, discountRate) : null;

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-3 flex flex-col relative w-full max-w-xs mx-auto">
      {/* Stokta yoksa üstte simge ve yazı */}
      {isOutOfStock && (
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold z-10">
          <FaTimesCircle className="w-6 h-6" />
          Stokta Yok
        </div>
      )}
      <Link to={`/product/${id}`}>
        <div className="w-full aspect-square mb-2 overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
          <img
            src={imageUrl || "/logo192.png"}
            alt={name}
            className="object-cover w-full h-full"
          />
        </div>
      </Link>
      <h3 className="font-bold text-base mb-1 line-clamp-2">{name}</h3>
      {campaignName && (
        <span className="text-xs text-green-700 bg-green-100 rounded px-2 py-1 mb-2 inline-block">
          {campaignName}
        </span>
      )}
      {/* Show participant info if campaign exists */}
      {typeof participantCount === "number" && typeof maxUserCount === "number" && (
        <div className="text-xs text-blue-700 mb-1">
          Katılımcı: {participantCount} / {maxUserCount}
        </div>
      )}
      <div className="text-gray-500 text-sm mb-1">
        {brand && <span>{brand}</span>}
        {brand && category && <span> • </span>}
        {category && <span>{category}</span>}
      </div>
      <div className="flex items-center mb-2">
        {discountedPrice ? (
          <>
            <span className="line-through text-gray-400 mr-2">{price.toLocaleString()} TL</span>
            <span className="text-green-600 font-bold">{discountedPrice.toLocaleString()} TL</span>
          </>
        ) : (
          <span className="font-bold">{price.toLocaleString()} TL</span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-auto">
        <input
          type="number"
          min={isOutOfStock ? 0 : 1}
          max={stock}
          value={isOutOfStock ? 0 : quantity}
          onChange={handleQuantityChange}
          className="w-12 border rounded px-1 py-1 text-sm"
          disabled={isOutOfStock}
        />
        <button
          onClick={handleAddToCart}
          className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 text-sm"
          disabled={!canAddToCart}
        >
          Sepete Ekle
        </button>
      </div>
      <div className="text-xs text-gray-500 mt-1">Stok: {stock}</div>
      {!isAuthenticated && hasCampaign && !isOutOfStock && (
        <div className="text-xs text-red-600 mt-2">
          Kampanyalı ürünleri sepete eklemek için giriş yapmalısınız.
        </div>
      )}
    </div>
  );
};

export default ProductCard;